import {UTCDate} from "@date-fns/utc";
import {differenceInHours, differenceInSeconds} from "date-fns";
import {FiAlertCircle, FiArrowRight} from "react-icons/fi";
import {Link} from "react-router";

import {Divider} from "@/components/divider/Divider.tsx";
import {encodeFilter} from "@/components/filterDropdown/FilterDropdown.ts";
import {Icon} from "@/components/icon/Icon.tsx";
import {KPIIcon} from "@/components/KPIIcon/KPIIcon.tsx";
import {KPIIconTypes} from "@/components/KPIIcon/KPIIcon.types.ts";
import {SummaryBar} from "@/components/summaryBar/SummaryBar.tsx";
import {autoDuration, durationSecFn, percentage, roundDurationSecs} from "@/lib/format.tsx";
import {DashboardGlobalMetrics} from "@/lib/types/website.ts";
import {
  responseTimeCSSClass,
  rumLoadTimeCSSClass,
  uptimePercentageCSSClass,
} from "@/pages/dashboard/display/dashboardUtils.ts";

interface DashboardMetricsProps {
  metrics: DashboardGlobalMetrics;
}

const formatNumOutages = (n: number): string => (n >= 100 ? "100+" : n.toString());

export const DashboardMetrics = ({metrics}: DashboardMetricsProps) => {
  const hasRecentAlert = differenceInHours(new UTCDate(), metrics.outages.last) < 2;

  return (
    <SummaryBar
      stats={metrics}
      summaryBlocks={[
        {
          title: "Checks",
          renderTitle: () => (
            <Link to="/checks" title="Show All Checks" className="pulseTextLink pulse-color-gray-700">
              <div className="d-flex gap-2 align-items-center">
                Checks
                <Icon icon={FiArrowRight} removeDivDimensions />
              </div>
            </Link>
          ),
          renderStat: ({checks}) => (
            <div>
              <div className="pt-2 pb-1 pulseTextH2Bold">{checks.total}</div>
              <Divider className="my-2" />
              <Link
                to={`/checks?filters=${encodeFilter({status: ["down"]})}`}
                title="Show All Down Checks"
                className="pulseTextBodySmall pulse-color-gray-600"
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex gap-2 align-items-center">
                    <KPIIcon type={KPIIconTypes.CHECKS_DOWN} />
                    Down
                  </div>
                  <div className="pulseTextBodySmallBold pulse-color-gray-900">{checks.down || 0}</div>
                </div>
              </Link>
              <Divider className="my-2" />
              <Link
                to={`/checks?filters=${encodeFilter({status: ["paused"]})}`}
                title="Show All Paused Checks"
                className="pulseTextBodySmall pulse-color-gray-600"
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex gap-2 align-items-center">
                    <KPIIcon type={KPIIconTypes.CHECKS_PAUSED} />
                    Paused
                  </div>
                  <div className="pulseTextBodySmallBold pulse-color-gray-900">{checks.paused || 0}</div>
                </div>
              </Link>
              <Divider className="my-2" />
              <Link
                to={`/checks?filters=${encodeFilter({status: ["maintenance"]})}`}
                title="Show All Maintenance Checks"
                className="pulseTextBodySmall pulse-color-gray-600"
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex gap-2 align-items-center">
                    <KPIIcon type={KPIIconTypes.CHECKS_MAINTENANCE} />
                    Maintenance
                  </div>
                  <div className="pulseTextBodySmallBold pulse-color-gray-900">{checks.maintenance || 0}</div>
                </div>
              </Link>
            </div>
          ),
        },
        {
          title: "Outages",
          renderTitle: () => (
            <Link to="/alerts" title="Show All Alerts" className="pulseTextLink pulse-color-gray-700">
              <div className="d-flex gap-2 align-items-center">
                Outages
                <Icon icon={FiArrowRight} removeDivDimensions />
              </div>
            </Link>
          ),
          renderStat: ({outages}) => (
            <div>
              <div className="pt-2 pb-1 pulseTextH2Bold">{formatNumOutages(outages["days_1"])}</div>
              <Divider className="my-2" />
              <div className="d-flex justify-content-between align-items-center py-1">
                <div className="pulseTextBodySmall pulse-color-gray-600">Last 24h</div>
                <div className="pulseTextBodySmallBold pulse-color-gray-900">{formatNumOutages(outages["days_1"])}</div>
              </div>
              <Divider className="my-2" />
              <div className="d-flex justify-content-between align-items-center py-1">
                <div className="pulseTextBodySmall pulse-color-gray-600">Last 7d</div>
                <div className="pulseTextBodySmallBold pulse-color-gray-900">{formatNumOutages(outages["days_7"])}</div>
              </div>
              <Divider className="my-2" />
              <div className="d-flex justify-content-between align-items-center py-1">
                <div className="pulseTextBodySmall pulse-color-gray-600">Last 30d</div>
                <div className="pulseTextBodySmallBold pulse-color-gray-900">
                  {formatNumOutages(outages["days_30"])}
                </div>
              </div>
            </div>
          ),
        },
        {
          title: "Response Time",
          renderStat: ({response_time}) => (
            <div>
              <Divider className="mb-2 mt-3" />
              <Link
                to={`/checks?filters=${encodeFilter({monitoring_service_type: ["HTTP"]})}`}
                title="Response time of HTTP Checks for the last 15m. Click to show all HTTP Checks"
                className="pulseTextBodySmall pulse-color-gray-600"
              >
                <div className="d-flex justify-content-between align-items-center py-1">
                  Website HTTP(S)
                  <div className="pulseTextBodySmallBold">
                    {response_time.HTTP.response_time_avg !== null ? (
                      <div
                        className={responseTimeCSSClass(
                          response_time.HTTP.response_time_avg,
                          response_time.HTTP.response_time_sla
                        )}
                      >
                        {autoDuration(response_time.HTTP.response_time_avg)}
                      </div>
                    ) : (
                      "n/a"
                    )}
                  </div>
                </div>
              </Link>
              <Divider className="my-2" />
              <Link
                to={`/checks?filters=${encodeFilter({monitoring_service_type: ["TRANSACTION"]})}`}
                title="Response time of Transaction Checks for the last 15m. Click to show all Transaction Checks"
                className="pulseTextBodySmall pulse-color-gray-600"
              >
                <div className="d-flex justify-content-between align-items-center py-1">
                  Transaction
                  <div className="pulseTextBodySmallBold">
                    {response_time.TRANSACTION.response_time_avg !== null ? (
                      <div
                        className={responseTimeCSSClass(
                          response_time.TRANSACTION.response_time_avg,
                          response_time.TRANSACTION.response_time_sla
                        )}
                      >
                        {autoDuration(response_time.TRANSACTION.response_time_avg)}
                      </div>
                    ) : (
                      "n/a"
                    )}
                  </div>
                </div>
              </Link>
              <Divider className="my-2" />
              <Link
                to={`/checks?filters=${encodeFilter({monitoring_service_type: ["API"]})}`}
                title="Response time of API Checks for the last 15m. Click to show all API Checks"
                className="pulseTextBodySmall pulse-color-gray-600"
              >
                <div className="d-flex justify-content-between align-items-center py-1">
                  API
                  <div className="pulseTextBodySmallBold">
                    {response_time.API.response_time_avg !== null ? (
                      <div
                        className={responseTimeCSSClass(
                          response_time.API.response_time_avg,
                          response_time.API.response_time_sla
                        )}
                      >
                        {autoDuration(response_time.API.response_time_avg)}
                      </div>
                    ) : (
                      "n/a"
                    )}
                  </div>
                </div>
              </Link>
            </div>
          ),
        },
        {
          title: "Other",
          renderStat: ({uptime, outages, response_time}) => (
            <div>
              <Divider className="mb-2 mt-3" />
              <div className="d-flex justify-content-between align-items-center py-1">
                <div className="pulseTextBodySmall pulse-color-gray-600">Global Uptime</div>
                <div className="pulseTextBodySmallBold">
                  {uptime.uptime_pct !== null ? (
                    <div className={uptimePercentageCSSClass(uptime.uptime_pct, uptime.uptime_sla)}>
                      {percentage(uptime.uptime_pct, 4)}
                    </div>
                  ) : (
                    "n/a"
                  )}
                </div>
              </div>
              <Divider className="my-2" />
              <Link to="/alerts" title="Time since last alert" className="pulseTextBodySmall pulse-color-gray-600">
                <div className="d-flex justify-content-between align-items-center py-1">
                  <div className="d-flex align-items-center">
                    {hasRecentAlert && (
                      <Icon icon={FiAlertCircle} className="pe-1 pulse-color-red-500" removeDivDimensions />
                    )}
                    Last Alert
                  </div>
                  <div className="pulseTextBodySmallBold">
                    {outages.last !== null ? (
                      <div className={hasRecentAlert ? "pulse-color-red-500" : "pulse-color-gray-900"}>
                        {durationSecFn(roundDurationSecs(differenceInSeconds(new UTCDate(), outages.last)))} ago
                      </div>
                    ) : (
                      "n/a"
                    )}
                  </div>
                </div>
              </Link>
              <Divider className="my-2" />
              <Link
                to="/rum/v2"
                title="Load time of RUM Checks for the current day. Click to show all RUM Checks"
                className="pulseTextBodySmall pulse-color-gray-600"
              >
                <div className="d-flex justify-content-between align-items-center py-1">
                  RUM Load Time
                  <div className="pulseTextBodySmallBold">
                    {response_time.RUM2.response_time_avg !== null ? (
                      <div className={rumLoadTimeCSSClass(response_time.RUM2.response_time_avg)}>
                        {autoDuration(response_time.RUM2.response_time_avg)}
                      </div>
                    ) : (
                      "n/a"
                    )}
                  </div>
                </div>
              </Link>
            </div>
          ),
        },
      ]}
      columns={{
        sm: 1,
        md: 2,
        lg: 2,
        xl: 4,
      }}
    />
  );
};
